/**
 * Bulk import for Tag Rules › Hours: rolling presets are refused (whole days
 * only) and `minVolume` is carried through. The AI prompt gets the same rules.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { CreateListTagRuleDto } from '@/hooks';

import { HOUR_DEFAULT_MIN_VOLUME, isHourExcludedPreset, parseMinVolume } from './hour-presets';

type BulkResult = { ok: true; rules: CreateListTagRuleDto[] } | { ok: false; error: string };

export const HOUR_BULK_PROMPT_NOTE = `

---

## Hour rules (read this — it overrides the date guidance above)
- These tags judge each of the 24 hours of the day on the recipients' local clock, per campaign. An hour is the hour of the visit, not of the send.
- Use whole days only: NEVER "last-24-hours" or "last-48-hours".
- Add "minVolume" to every rule: the unique visits an hour needs before the tag judges it (default ${HOUR_DEFAULT_MIN_VOLUME}). Below it the hour is Unknown and the Drop Generator never acts on it.
- Typical tags: "Golden hour" (high EPC/ROI), "Dead hour" (spend with no return).`;

function rawRules(raw: unknown): Record<string, unknown>[] {
  const rules = (raw as { rules?: unknown })?.rules;
  return Array.isArray(rules) ? (rules as Record<string, unknown>[]) : [];
}

/** Runs after the shared bulk validation; same order as its `rules`. */
export function applyHourBulkRules(raw: unknown, rules: CreateListTagRuleDto[]): BulkResult {
  const source = rawRules(raw);
  const out: CreateListTagRuleDto[] = [];
  for (const [i, dto] of rules.entries()) {
    const preset = String(dto.dateRangePreset ?? 'last-30-days');
    if (isHourExcludedPreset(preset)) {
      return {
        ok: false,
        error: `Rule ${i + 1}: "${preset}" is not available for hour tags — use whole days (today, yesterday, last-N-days … or custom)`,
      };
    }
    const rawVolume = source[i]?.minVolume;
    const minVolume = rawVolume === undefined ? undefined : parseMinVolume(rawVolume);
    if (minVolume === null) {
      return { ok: false, error: `Rule ${i + 1}: minVolume must be a whole number from 0 to 1,000,000` };
    }
    out.push(minVolume === undefined ? dto : { ...dto, minVolume });
  }
  return { ok: true, rules: out };
}
